const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR - 1999 }, (_, i) => CURRENT_YEAR - i);

const SOURCE_TYPES = [
  { label: 'Academic Papers', icon: 'ri-file-paper-2-line', color: 'text-violet-600' },
  { label: 'News Articles', icon: 'ri-newspaper-line', color: 'text-emerald-600' },
  { label: 'Research Studies', icon: 'ri-microscope-line', color: 'text-cyan-600' },
  { label: 'Books', icon: 'ri-book-open-line', color: 'text-amber-600' },
  { label: 'Videos', icon: 'ri-play-circle-line', color: 'text-rose-600' },
];

const CITATION_PRESETS = [
  { label: 'Any', value: 0 },
  { label: '5+', value: 5 },
  { label: '10+', value: 10 },
  { label: '25+', value: 25 },
  { label: '50+', value: 50 },
  { label: '100+', value: 100 },
];

export interface AdvancedFilters {
  dateFrom: number | null;
  dateTo: number | null;
  excludedTypes: Set<string>;
  minCitations: number;
}

interface Props {
  filters: AdvancedFilters;
  onDateFromChange: (year: number | null) => void;
  onDateToChange: (year: number | null) => void;
  onToggleExcludeType: (type: string) => void;
  onMinCitationsChange: (n: number) => void;
  onReset: () => void;
}

export default function AdvancedSearchPanel({
  filters,
  onDateFromChange,
  onDateToChange,
  onToggleExcludeType,
  onMinCitationsChange,
  onReset,
}: Props) {
  const { dateFrom, dateTo, excludedTypes, minCitations } = filters;

  return (
    <div className="mt-3 bg-white border border-slate-200 rounded-2xl overflow-hidden animate-in">
      {/* Panel header bar */}
      <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-slate-50 to-slate-50 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-slate-700 flex items-center justify-center">
            <i className="ri-equalizer-2-line text-white text-xs"></i>
          </div>
          <span className="text-sm font-bold text-slate-800">Advanced Search Filters</span>
        </div>
        <button
          onClick={onReset}
          className="text-xs text-slate-400 hover:text-red-500 font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
        >
          <i className="ri-refresh-line text-xs"></i>
          Reset all
        </button>
      </div>

      <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* ── Date Range ── */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-calendar-range-line text-slate-500 text-base"></i>
            </div>
            <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">Publication Year</span>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex-1">
              <label className="text-[10px] text-slate-400 font-semibold mb-1 block uppercase tracking-wide">From</label>
              <select
                value={dateFrom ?? ''}
                onChange={(e) => onDateFromChange(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent cursor-pointer appearance-none"
              >
                <option value="">Any</option>
                {YEAR_OPTIONS.map(y => (
                  <option key={y} value={y} disabled={dateTo !== null && y > dateTo}>{y}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center justify-center mt-5 text-slate-400 text-xs font-bold flex-shrink-0">
              —
            </div>

            <div className="flex-1">
              <label className="text-[10px] text-slate-400 font-semibold mb-1 block uppercase tracking-wide">To</label>
              <select
                value={dateTo ?? ''}
                onChange={(e) => onDateToChange(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent cursor-pointer appearance-none"
              >
                <option value="">Any</option>
                {YEAR_OPTIONS.map(y => (
                  <option key={y} value={y} disabled={dateFrom !== null && y < dateFrom}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Active date range summary */}
          {(dateFrom || dateTo) && (
            <div className="mt-2 flex items-center gap-1.5 px-2.5 py-1.5 bg-violet-50 border border-violet-200 rounded-lg">
              <i className="ri-checkbox-circle-fill text-violet-500 text-xs"></i>
              <span className="text-[11px] font-semibold text-violet-700">
                {dateFrom && dateTo ? `${dateFrom} – ${dateTo}` : dateFrom ? `From ${dateFrom}` : `Up to ${dateTo}`}
              </span>
            </div>
          )}
        </div>

        {/* ── Source Type Exclusions ── */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-filter-off-line text-slate-500 text-base"></i>
            </div>
            <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">Exclude Source Types</span>
          </div>

          <div className="space-y-2">
            {SOURCE_TYPES.map(({ label, icon, color }) => {
              const excluded = excludedTypes.has(label);
              return (
                <button
                  key={label}
                  onClick={() => onToggleExcludeType(label)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border text-left transition-all cursor-pointer ${
                    excluded
                      ? 'bg-red-50 border-red-200 opacity-60'
                      : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${icon} text-sm ${excluded ? 'text-red-400' : color}`}></i>
                  </div>
                  <span className={`text-xs font-semibold flex-1 ${excluded ? 'text-red-500 line-through' : 'text-slate-700'}`}>
                    {label}
                  </span>
                  <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border transition-all ${
                    excluded ? 'bg-red-500 border-red-500' : 'border-slate-300 bg-white'
                  }`}>
                    {excluded && <i className="ri-close-line text-white text-[10px]"></i>}
                  </div>
                </button>
              );
            })}
          </div>

          {excludedTypes.size > 0 && (
            <p className="mt-2 text-[11px] text-red-500 font-medium flex items-center gap-1">
              <i className="ri-error-warning-line text-xs"></i>
              {excludedTypes.size} type{excludedTypes.size > 1 ? 's' : ''} excluded from results
            </p>
          )}
        </div>

        {/* ── Minimum Citation Count ── */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-double-quotes-r text-slate-500 text-base"></i>
            </div>
            <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">Min. Citations</span>
          </div>

          {/* Preset chips */}
          <div className="grid grid-cols-3 gap-1.5 mb-3">
            {CITATION_PRESETS.map(({ label, value }) => (
              <button
                key={value}
                onClick={() => onMinCitationsChange(value)}
                className={`py-2 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap border ${
                  minCitations === value
                    ? 'bg-violet-600 text-white border-violet-600'
                    : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Custom input */}
          <div>
            <label className="text-[10px] text-slate-400 font-semibold mb-1 block uppercase tracking-wide">Custom minimum</label>
            <div className="relative">
              <input
                type="number"
                min={0}
                max={10000}
                value={minCitations === 0 ? '' : minCitations}
                onChange={(e) => {
                  const v = parseInt(e.target.value);
                  onMinCitationsChange(isNaN(v) || v < 0 ? 0 : v);
                }}
                placeholder="e.g. 75"
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-slate-400 pointer-events-none">
                citations
              </span>
            </div>
          </div>

          {minCitations > 0 && (
            <div className="mt-2 flex items-center gap-1.5 px-2.5 py-1.5 bg-violet-50 border border-violet-200 rounded-lg">
              <i className="ri-checkbox-circle-fill text-violet-500 text-xs"></i>
              <span className="text-[11px] font-semibold text-violet-700">
                Showing sources cited {minCitations}+ times
              </span>
            </div>
          )}

          {/* Info note */}
          <p className="mt-3 text-[10px] text-slate-400 leading-relaxed">
            Higher citation counts typically indicate more widely accepted and peer-reviewed research.
          </p>
        </div>
      </div>
    </div>
  );
}
